
import { TimerState, TimerType, TimerSettings } from './timerTypes';
import { getTotalTimeForType } from './timerUtils';

export const handleResetTimer = (
  state: TimerState,
  timerType: TimerType,
  startRunning: boolean | undefined,
  sequencePos: number | undefined,
  settings: TimerSettings
): TimerState => {
  const timeLeft = getTotalTimeForType(timerType, settings);
  
  const newState = {
    ...state,
    timerType,
    timeLeft,
    elapsedSeconds: 0,
    progress: 0 // Reset progress to 0 when timer resets
  };
  
  // Handle auto-start logic
  const isFirstPomodoro = sequencePos === 0 && timerType === 'pomodoro' && state.isFirstStart;
  
  if (startRunning !== undefined) {
    newState.isRunning = startRunning;
  } else {
    newState.isRunning = !isFirstPomodoro;
  }
  
  // Update first start if we're auto-starting
  if (startRunning || !isFirstPomodoro) {
    newState.isFirstStart = false;
  }
  
  // Update sequence position if provided
  if (sequencePos !== undefined) {
    newState.sequencePosition = sequencePos;
  }
  
  return newState;
};

export const handleToggleTimer = (
  state: TimerState,
  settings?: TimerSettings
): TimerState => {
  const newIsRunning = !state.isRunning;
  let newState = { ...state, isRunning: newIsRunning };
  
  if (state.isFirstStart && newIsRunning) {
    newState.isFirstStart = false;
  }
  
  if (newIsRunning && state.timeLeft === 0) {
    // Reset the timer if it's completed and the user presses play again
    const defaultSettings = { pomodoro: 25, shortBreak: 5, longBreak: 15, sessions: 4, autoRepeat: true };
    const totalTime = getTotalTimeForType(state.timerType, settings || defaultSettings);
    return {
      ...newState,
      timeLeft: totalTime,
      elapsedSeconds: 0,
      progress: 0
    };
  }
  
  return newState;
};

export const handleSetProgress = (state: TimerState, progress: number): TimerState => {
  return {
    ...state,
    progress
  };
};

export const handleSetTimeLeft = (state: TimerState, timeLeft: number): TimerState => {
  // Calculate progress properly: progress = how much time has elapsed as a percentage
  // We need to know the total time for this timer type to calculate progress correctly
  const totalTime = state.elapsedSeconds + timeLeft;
  const progress = totalTime > 0 ? (state.elapsedSeconds / totalTime) * 100 : 0;
  
  return {
    ...state,
    timeLeft,
    progress,
    elapsedSeconds: state.elapsedSeconds + 1 // Increment elapsed time when time decreases
  };
};

export const handleSetRunning = (state: TimerState, isRunning: boolean): TimerState => {
  return {
    ...state,
    isRunning
  };
};

export const handleIncrementPomodoros = (state: TimerState): TimerState => {
  return {
    ...state,
    completedPomodoros: state.completedPomodoros + 1
  };
};

export const handleSetFirstStart = (state: TimerState, isFirstStart: boolean): TimerState => {
  return {
    ...state,
    isFirstStart
  };
};

export const handleSetSessionIndex = (state: TimerState, index: number): TimerState => {
  return {
    ...state,
    currentSessionIndex: index
  };
};

export const handleSetSequencePosition = (state: TimerState, position: number): TimerState => {
  return {
    ...state,
    sequencePosition: position
  };
};

export const handleUpdateSettings = (state: TimerState, settings: TimerSettings): TimerState => {
  const newTimeLeft = getTotalTimeForType(state.timerType, settings);
  // Reset progress when settings change
  return {
    ...state,
    timeLeft: newTimeLeft,
    elapsedSeconds: 0,
    progress: 0
  };
};
